import {Injectable} from "@angular/core";
import {Event, NavigationEnd, Router} from "@angular/router";

import {BehaviorSubject} from "rxjs";
import {SimpleCryptoService} from '../crypto/simple-crypto.service';
import {environment} from '../../../../../environments/environment';
import {User} from '../../../../shared/models/user/user';
import {FilterByPage} from '../../../../interfaces/filtres/filter-interface';
import {RouteId} from '../../../../enums/env/routes-id-enum';

@Injectable({
  providedIn: "root",
})
export class UserSessionService {
  public authToken!: string;
  public dataToken!: string; // like 'Bearer'
  public user!: any;
  public version!: string;
  public authToken$ = new BehaviorSubject<string | null>(null);

  currentUser$: BehaviorSubject<User | null>

  constructor(
    private router: Router,
    private simpleCryptoService: SimpleCryptoService
  ) {
    const user = this.getUser();
    this.currentUser$ = new BehaviorSubject<User | null>(user ?? null);
  }

  /**
   * Get a value encrypted or not to plain value (depending of environment conf)
   */
  private getEncryptedToDecryptedValue(value: any, mustDecodeIfObject: boolean) {
    if (environment.encryptUserSession) {
      value = this.simpleCryptoService.decryptByValue(
        value,
        mustDecodeIfObject
      );
    } else if (mustDecodeIfObject) {
      value = value ? this.getParsableJson(value) : null;
    }
    return value;
  }

  /**
   * Get a value decrypted or not to encrypted value (depending of environment conf)
   */
  private getDecryptedToEncryptedValue(value: any) {
    let newValue = value;
    if (environment.encryptUserSession) {
      newValue = this.simpleCryptoService.encryptByValue(value);
    }
    return newValue;
  }

  /**
   * Check if a value is a valid JSON or return null
   */
  getParsableJson(value: any): boolean | null {
    try {
      const json = JSON.parse(value);
      return json;
    } catch (error) {
      return null;
    }
  }

  // Get authentication token from session
  public getAuthToken(): string {
    const authToken = localStorage.getItem(environment.authTokenNameInSession);
    this.authToken$.next(authToken);
    return this.getEncryptedToDecryptedValue(authToken, false);
  }

  // Store authentication token in session
  public storeAuthToken(authToken: string): void {
    authToken = this.getDecryptedToEncryptedValue(authToken);
    this.authToken$.next(authToken);
    localStorage.setItem(environment.authTokenNameInSession, authToken);
  }

  // Get expiration of autentication
  public getAuthExpireAt(): number | null {
    let expireAtString = localStorage.getItem(
      environment.authExpireAtNameInSession
    );
    expireAtString = this.getEncryptedToDecryptedValue(expireAtString, false);
    if (expireAtString) {
      const expireAt = Number(expireAtString);
      const isNumber = !isNaN(expireAt) ? true : null;
      if (isNumber) {
        return expireAt;
      }
    }

    return null;
  }

  // Store expiration of authentication in session
  public storeAuthExpireAt(expireAt: number): void {
    const expireAtString = this.getDecryptedToEncryptedValue(
      expireAt.toString()
    );
    localStorage.setItem(environment.authExpireAtNameInSession, expireAtString);
  }

  // Get token type to consume data. Eg. 'Bearer' for an api
  public getDataTokenType(): string {
    const dataTokenType = localStorage.getItem(
      environment.dataTokenTypeNameInSession
    );
    return this.getEncryptedToDecryptedValue(dataTokenType, false);
  }

  // Store token type to consume data in session
  public storeDataTokenType(dataTokenType: string): void {
    dataTokenType = this.getDecryptedToEncryptedValue(dataTokenType);
    localStorage.setItem(environment.dataTokenTypeNameInSession, dataTokenType);
  }

  // Get token used to consume data Eg. a token for an api
  public getDataToken(): string {
    const dataToken = localStorage.getItem(environment.dataTokenNameInSession);
    return this.getEncryptedToDecryptedValue(dataToken, false);
  }

  // Store token used to consume data. Eg. For an API
  public storeDataToken(dataToken: string): void {
    dataToken = this.getDecryptedToEncryptedValue(dataToken);
    localStorage.setItem(environment.dataTokenNameInSession, dataToken);
  }

  // Get User object in session
  public getUser(): User {
    const user = localStorage.getItem(environment.authUserNameInSession);
    const decryptedUser = this.getEncryptedToDecryptedValue(user, true);
    const userModel = new User();
    userModel.initFromSession(decryptedUser);

    return this.parseUser(user);
  }

  // Store User object in session
  public storeUser(user: string) {
    user = this.getDecryptedToEncryptedValue(user);
    localStorage.setItem(environment.authUserNameInSession, user);

    this.currentUser$.next(this.parseUser(user));
  }

  parseUser(user: string | null): User {
    const decryptedUser = this.getEncryptedToDecryptedValue(user, true);
    const userModel = new User();

    userModel.initFromSession(decryptedUser);

    return userModel;
  }

  // Get the app version in session
  public getVersion() {
    return localStorage.getItem(environment.versionNameInSession);
  }

  // Store the app version in session
  public storeVersion(value: string): void {
    localStorage.setItem(environment.versionNameInSession, value);
  }

  // Get the redirectTo in session (redirection after logged in)
  public getRedirectTo() {
    return localStorage.getItem(environment.authRedirectToNameInSession);
  }

  public clearSessionSearch() {
    sessionStorage.setItem(
      environment.search,
      JSON.stringify([
        {
          type: RouteId.STATIONS,
          params: [],
        },
      ])
    );
  }

  // Get the search tab in session
  public getSearch<T>(): FilterByPage<T>[] {
    const searchTab =
      JSON.parse(sessionStorage.getItem(environment.search) as string) !== null
        ? JSON.parse(sessionStorage.getItem(environment.search) as string)
        : [];
    return searchTab;
  }

  // Store the redirectTo in session (redirection after logged in)
  public setRedirectTo(value: string): void {
    localStorage.setItem(environment.authRedirectToNameInSession, value);
  }

  // Clean User session
  public cleanSession(): void {
    localStorage.removeItem(environment.authTokenNameInSession);
    localStorage.removeItem(environment.authExpireAtNameInSession);
    localStorage.removeItem(environment.dataTokenTypeNameInSession);
    localStorage.removeItem(environment.dataTokenNameInSession);
    localStorage.removeItem(environment.authUserNameInSession);
    localStorage.removeItem(environment.versionNameInSession);
    localStorage.removeItem(environment.versionNameInSession);
    // remove saved search in case some search parameters are not available anymore
    localStorage.removeItem("apiSearch");
    // localStorage.removeItem(environment.authRedirectToNameInSession);

    this.cleanTableFiltersRelated();
    this.cleanPaginationRelated();

    this.currentUser$.next(null);
  }

  private cleanTableFiltersRelated() {
    // Object.keys(localStorage)
    //   .filter((item) => item.startsWith(TABLE_ID_PREFIX))
    //   .forEach((item) => localStorage.removeItem(item));
  }

  private cleanPaginationRelated() {
    // Object.keys(localStorage)
    //   .filter((item) => item.startsWith(PAGINATION_ID_PREFIX))
    //   .forEach((item) => localStorage.removeItem(item));
  }

  public versionListener() {
    this.router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.checkWebappVersion();
      }
    });
  }

  /**
   * Check application version (& update it if new deployment).
   */
  public checkWebappVersion(): boolean {
    let canContinue = true;
    // current webapp version
    const currentVersion = environment.VERSION;
    const storedVersion = this.getVersion();

    if (!currentVersion) {
      console.error('Need to define a "version" in environment');
    } else {
      if (!storedVersion) {
        // console.log('No stored version, setting current version.');
        this.storeVersion(currentVersion);
        // if current version is different from the stored
      } else if (!!storedVersion && storedVersion !== currentVersion) {
        // clear all, set new version & log back in
        this.cleanSession();
        this.storeVersion(currentVersion);
        document.location.reload();
        canContinue = false;
      }
    }
    return canContinue;
  }

  // getCurrentLanguage(): string {
  //   return localStorage.getItem("language");
  // }
}
